import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { recruitingAPI } from '../../lib/api';
import { Gift, Copy, Users, CheckCircle2, Euro, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const money = (v, cur) => `${Number(v || 0).toLocaleString('de-DE')} ${cur || 'EUR'}`;
const BONUS_LABEL = { offen: 'offen', anspruch: 'auszahlbar', ausgezahlt: 'ausgezahlt' };
const BONUS_VARIANT = { offen: 'secondary', anspruch: 'default', ausgezahlt: 'outline' };

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    recruitingAPI.getMyReferral()
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || data.enabled === false) return null;

  const link = `${window.location.origin}/karriere?ref=${data.code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success('Empfehlungslink kopiert'); }
    catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  return (
    <Card className="border-primary/20 bg-primary/[0.03]" data-testid="referral-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Mitarbeiter werben Mitarbeiter</h3>
          {data.bonus_amount > 0 && (
            <Badge className="ml-auto">{money(data.bonus_amount, data.currency)} pro Einstellung</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Teile deinen persönlichen Link. Wird jemand über deinen Link eingestellt, erhältst du die Prämie.
        </p>

        <div className="flex gap-2">
          <Input readOnly value={link} className="text-xs" data-testid="referral-link" onFocus={(e) => e.target.select()} />
          <Button variant="outline" size="icon" onClick={copy} data-testid="copy-referral-btn" title="Kopieren">
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border p-3 text-center">
            <Users className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-bold">{data.stats.referred}</p>
            <p className="text-[11px] text-muted-foreground">Geworben</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-500 mb-1" />
            <p className="text-lg font-bold">{data.stats.hired}</p>
            <p className="text-[11px] text-muted-foreground">Eingestellt</p>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <Euro className="h-4 w-4 mx-auto text-amber-500 mb-1" />
            <p className="text-lg font-bold">{data.bonus_amount > 0 ? money(data.stats.earned_total, data.currency) : '—'}</p>
            <p className="text-[11px] text-muted-foreground">Prämie gesamt</p>
          </div>
        </div>

        {data.referrals.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">Deine Empfehlungen</p>
            {data.referrals.slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b py-1.5 last:border-0">
                <span className="truncate mr-2">{r.first_name} {r.last_name} · <span className="text-muted-foreground">{r.job_title}</span></span>
                {r.referral_bonus_status && (
                  <Badge variant={BONUS_VARIANT[r.referral_bonus_status]} className="shrink-0 text-[10px]">
                    {BONUS_LABEL[r.referral_bonus_status]}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
